import type { ReactNode } from 'react';
import { ERRORS } from '@locales/id/errors';
import { ERROR_CODE } from '@lib/api/errors';
import { COMMON } from '@locales/id/messages';
import { ErrorState } from './ErrorState';
import { LoadFailed } from './States';
import { SkeletonCards, SkeletonDelay } from './Skeleton';
import { useLoadingGate } from './useLoadingGate';

interface State<T> { data: T | null; error: string | null; errorCode?: string | null; loading: boolean; retry: () => void }

/** Answers that mean "not for you to see": gone, or outside the account's region (the backend answers 404 for both). */
const MISSING = new Set<string>([ERROR_CODE.NOT_FOUND, ERROR_CODE.OUT_OF_SCOPE]);

/** Lists answer arrays or pages ({ items }): either is empty when it has no rows. */
const defaultEmpty = (d: unknown) => (Array.isArray(d) ? d.length === 0 : !!d && typeof d === 'object' && Array.isArray((d as { items?: unknown }).items) ? (d as { items: unknown[] }).items.length === 0 : false);

/**
 * Every state of a `useApi` result in one place:
 * - first load: nothing for a moment, then `skeleton` (kept briefly, so it never flickers);
 * - failure without data: the error with "Coba lagi"; a record that does not exist or is outside the account's
 *   region: `missing` (when given), never a retry that cannot help; a failure over data on screen: a banner above it;
 * - no rows: `empty` (when given);
 * - a refetch (filters changed): the old data stays, with a thin bar on top.
 */
export function Async<T>({ state, children, label, skeleton, empty, missing, isEmpty = defaultEmpty }: { state: State<T>; children: (d: T) => ReactNode; label?: string; skeleton?: ReactNode; empty?: ReactNode; missing?: ReactNode; isEmpty?: (d: T) => boolean }) {
  const waiting = state.loading && state.data === null;
  const showSkeleton = useLoadingGate(waiting);
  if (showSkeleton) return <SkeletonDelay.Provider value={false}>{skeleton ?? <SkeletonCards label={label} />}</SkeletonDelay.Provider>;
  if (waiting) return null;
  if (state.data === null && missing !== undefined && state.errorCode && MISSING.has(state.errorCode)) return <>{missing}</>;
  if (state.data === null) return <LoadFailed error={state.error ?? ERRORS.loadFailed} onRetry={state.retry} />;
  return (
    <>
      {state.loading && <div className="refresh-bar" role="status" aria-label={COMMON.refreshing} />}
      {state.error && <div className="pad-x"><ErrorState error={`${ERRORS.stale}${state.error}`} onRetry={state.retry} /></div>}
      {empty !== undefined && isEmpty(state.data) ? empty : children(state.data)}
    </>
  );
}
